import { DIMENSION_IDS } from '@/constants'
import { breakpointsTailwind } from '@vueuse/core'
import { defaultExportedChart, defineChart } from '..'
import { tooltip } from '@/composables/useChart/theme'
import type {
    BarSeriesOption,
    EChartsOption,
    LabelLayoutOptionCallback
} from 'echarts'
import type { Config } from '@/composables/useParams'
import type { RectLike } from 'zrender'
import type { ValueAxisBaseOption } from 'echarts/types/src/coord/axisCommonTypes'
import type { Values } from '@/composables/useValues'

const labelLineLength = 20

type Params = [Values, Values]

/**
 * Create the world bar chart options.
 */
export default defineChart<Params>((data, comparison) => {
    if (!data.dimension || !data.data) return {}

    const { config } = useParams()

    const categories = config.showRegions
        ? data.regionalSorted.map(({ iso }) => iso)
        : data.sorted.map(({ iso }) => iso)

    const itemNameKey = 'iso'

    return {
        tooltip: {},
        grid: [{}, {}],
        dataset: [
            {
                source: config.showRegions
                    ? data.regionalData
                    : data.sorted ?? [],
                dimensions: ['value', 'score', 'total', itemNameKey]
            },
            {
                source: config.showRegions
                    ? comparison.regionalData
                    : comparison.data ?? [],
                dimensions: ['value', 'score', 'total', itemNameKey]
            }
        ],
        xAxis: [{}, {}],
        yAxis: [{}, {}],
        dataZoom: [
            {
                type: 'slider',
                orient: 'vertical',
                right: 0,
                width: 10,
                start: 0,
                end: 100,
                show: false,
                filterMode: 'filter',
                yAxisIndex: [0, 1],
                moveOnMouseMove: true,
                moveOnMouseWheel: true
            },
            {
                show: false,
                type: 'inside',
                moveOnMouseMove: true,
                moveOnMouseWheel: true,
                yAxisIndex: [0, 1]
            }
        ],
        media: createBarChartMediaQuery(
            data,
            comparison,
            config,
            itemNameKey,
            categories
        ),
        series: [
            createSeries(config, data, 0, itemNameKey),
            createSeries(config, comparison, 1, itemNameKey)
        ],

        export: defaultExportedChart({
            grid: comparison.data
                ? [
                      {
                          top: '10%',
                          height: '35%',
                          containLabel: false
                      },
                      {
                          height: '35%',
                          top: '45%',
                          containLabel: false
                      }
                  ]
                : [{ top: '10%', bottom: '20%' }]
        })
    }
})

/**
 * Switch to log axis when dimension is prevalence.
 */
function getValueAxisType(data: Values) {
    const { isPrevalence } = useFramework()

    return isPrevalence(data.dimension) && !data.isAbsolute ? 'log' : 'value'
}

/**
 * Create a value axis.
 */
function createValueAxis(
    gridIndex: number,
    values: Values,
    hasComparison?: boolean
) {
    const type = getValueAxisType(values)

    return {
        type,
        gridIndex,
        name: values.dimension?.name,
        max: type === 'log' ? 1000 : undefined,
        min: type === 'log' ? 0.01 : undefined,
        inverse: gridIndex === 1,
        position: 'left',
        boundaryGap:
            hasComparison && values.dimension?.id === DIMENSION_IDS.GOV_RESPONSE
                ? [-1, 0]
                : undefined,
        axisLabel: {
            align: 'right',
            formatter(value: number, index: number) {
                if (type === 'log' && index === 0) return 0

                return gridIndex === 0 || index > 0
                    ? values.formatter(value)
                    : ''
            }
        }
    } as ValueAxisBaseOption
}

/**
 * Create a category axis.
 */
function createCategoryAxis(gridIndex: number, data: string[], config: Config) {
    return {
        type: 'category',
        gridIndex,
        z: 1000,
        name:
            config.compareDimension && gridIndex === 0
                ? ''
                : config.showRegions
                ? 'Regions'
                : 'Countries',
        show: gridIndex === 0 || config.compareDimension,
        data: data,
        axisLine: {
            show: gridIndex === 0,
            lineStyle: {
                width: 2,
                color: 'black'
            }
        },
        axisTick: {
            show: false
        }
    }
}

/**
 * Create a bar series for the given dimension.
 */
function createSeries(
    _config: Config,
    data: Values,
    gridIndex: number,
    itemNameKey: string
) {
    const { valueKey, formatter, dimension, isAbsolute, regionalAverages } =
        data

    return {
        tooltip: {
            formatter(params: any) {
                const value = params.value?.[valueKey] as number | undefined
                if (value === undefined) return ''
                const country = params.name as string
                return tooltip(dimension?.name ?? '', country, formatter(value))
            }
        },
        datasetIndex: gridIndex,
        xAxisIndex: gridIndex,
        yAxisIndex: gridIndex,
        type: 'bar',
        barGap: '-100%',
        barCategoryGap: '15%',
        z: -gridIndex,
        emphasis: {
            focus: 'self',
            blurScope: 'global',
            itemStyle:
                gridIndex === 0
                    ? {}
                    : {
                          color: twColor('gray')
                      }
        },
        encode: {
            itemName: itemNameKey,
            x: itemNameKey,
            y: valueKey
        },
        barMaxWidth: 225,
        labelLine: {
            length2: labelLineLength,
            showAbove: true,
            lineStyle: {
                color: 'black'
            }
        },
        label: {
            show: gridIndex === 0,
            position: 'top',
            fontFamily: 'Rubik',
            align: 'left',
            color: 'black',
            minMargin: 100,
            fontSize: 12
        },
        labelLayout,
        universalTransition: {
            enabled: true
        },
        itemStyle:
            gridIndex === 0
                ? {}
                : {
                      color: twColor('gray', 50)
                  },
        markLine: dimension
            ? {
                  name: 'Average',
                  symbol: 'none',
                  silent: true,
                  lineStyle: {
                      color: 'black'
                  },
                  label: {
                      formatter(params) {
                          return `${params.name} (${formatter(
                              Number(params.value)
                          )})`
                      },
                      position: 'insideEndTop',
                      verticalAlign: 'middle',
                      offset: [0, -5],
                      fontWeight: 'bold',
                      fontFamily: 'Source Sans Pro',
                      fontSize: 16,
                      color: 'black',
                      textBorderWidth: 0
                  },
                  data: regionalAverages.map(({ name, value, score }) => ({
                      name,
                      yAxis: isAbsolute ? value : score
                  }))
              }
            : {}
    } as BarSeriesOption
}

/**
 * Create a default media query option.
 */
function createDefaultMediaOption(
    data: Values,
    comparison: Values,
    config: Config,
    itemNameKey: string,
    categories: string[]
) {
    const hasComparison = Boolean(comparison.dimension)

    return {
        grid: hasComparison
            ? [
                  {
                      left: '10%',
                      top: '15%',
                      height: '37.5%',
                      bottom: '0%'
                  },
                  {
                      left: '10%',
                      top: '52.5%',
                      height: '37.5%',
                      bottom: '0%'
                  }
              ]
            : [
                  {
                      bottom: '10%',
                      top: '15%'
                  },
                  {
                      bottom: '10%',
                      top: '15%'
                  }
              ],
        dataZoom: [
            {
                show: false,
                start: 0
            },
            {
                show: false
            }
        ],
        yAxis: [
            createValueAxis(0, data, Boolean(comparison.dimension)),
            createValueAxis(1, comparison, Boolean(comparison.dimension))
        ],
        xAxis: [
            createCategoryAxis(0, categories, config),
            createCategoryAxis(1, categories, config)
        ],
        series: [
            {
                labelLayout,
                labelLine: {
                    show: true
                },
                emphasis: {
                    label: { show: true }
                },
                label: {
                    overflow: ''
                },
                encode: {
                    x: itemNameKey,
                    y: data.valueKey
                }
            },
            {
                labelLayout,
                encode: {
                    x: itemNameKey,
                    y: comparison.valueKey
                },
                label: {
                    show: false,
                    overflow: ''
                },
                labelLine: {
                    show: true
                }
            }
        ]
    }
}

/**
 * Create a mobile media query option.
 */
function createMobileMediaOption(
    data: Values,
    comparison: Values,
    config: Config,
    itemNameKey: string,
    categories: string[]
) {
    const hasComparison = Boolean(comparison.dimension)
    const labelLayout = labelLayoutMobile()

    return {
        grid: hasComparison
            ? [
                  {
                      left: '10%',
                      width: '39%',
                      top: '5%',
                      bottom: '10%',
                      right: '0%'
                  },
                  {
                      left: '49%',
                      width: '39%',
                      top: '5%',
                      bottom: '10%',
                      right: '12%'
                  }
              ]
            : [
                  {
                      top: '5%',
                      bottom: '10%',
                      right: '5%',
                      left: '5%'
                  },
                  {}
              ],
        yAxis: [
            {
                ...createCategoryAxis(0, categories, config),
                show: false
            },
            {
                ...createCategoryAxis(1, categories, config),
                show: false
            }
        ],
        xAxis: [
            {
                ...createValueAxis(0, data, Boolean(comparison.dimension)),
                name: '',
                max: undefined,
                min: undefined,
                inverse: hasComparison,
                splitNumber: data.isAbsolute ? 2 : undefined
            },
            {
                ...createValueAxis(
                    1,
                    comparison,
                    Boolean(comparison.dimension)
                ),
                name: '',
                max: undefined,
                min: undefined,
                show: hasComparison,
                inverse: false,
                splitNumber: comparison.isAbsolute ? 2 : undefined
            }
        ],
        dataZoom: [
            {
                show: true,
                start: 90
            },
            {
                show: true
            }
        ],
        series: [
            {
                labelLayout,
                encode: {
                    y: itemNameKey,
                    x: data.valueKey
                },
                label: {
                    overflow: 'truncate'
                },
                emphasis: {
                    label: { show: false }
                },
                labelLine: {
                    show: false
                }
            },
            {
                labelLayout,
                label: {
                    show: true
                },
                emphasis: {
                    label: { show: false }
                },
                encode: {
                    y: itemNameKey,
                    x: comparison.valueKey
                },
                labelLine: {
                    show: false
                }
            }
        ]
    }
}

/**
 * Create bar chart media query.
 */
function createBarChartMediaQuery(
    data: Values,
    comparison: Values,
    config: Config,
    itemNameKey: string,
    categories: string[]
) {
    return [
        {
            query: {
                maxWidth: breakpointsTailwind.md
            },
            option: createMobileMediaOption(
                data,
                comparison,
                config,
                itemNameKey,
                categories
            )
        },
        {
            query: {
                minWidth: breakpointsTailwind.md
            },
            option: createDefaultMediaOption(
                data,
                comparison,
                config,
                itemNameKey,
                categories
            )
        }
    ] as EChartsOption['media']
}

/**
 * Default layout.
 */
export const labelLayout: LabelLayoutOptionCallback = params => {
    const { rect, labelRect, align } = params
    const labelLinePoints = [
        [rect.x + rect.width / 2, rect.y],
        [rect.x + rect.width / 2, rect.y - labelLineLength]
    ]

    if (align === 'left') {
        labelLinePoints.push([
            rect.x + rect.width / 2 + labelRect.width,
            rect.y - labelLineLength
        ])
    } else if (align === 'right') {
        labelLinePoints.push([
            rect.x + rect.width - labelRect.width,
            rect.y - labelLineLength
        ])
    } else {
        labelLinePoints.push(
            [rect.x + labelRect.width / 2, rect.y - labelLineLength],
            [rect.x - labelRect.width / 2, rect.y - labelLineLength]
        )
    }

    return {
        ...params,
        hideOverlap: true,
        moveOverlap: undefined,
        width: labelRect.width,
        dy: -labelLineLength,
        labelLinePoints
    }
}

/**
 * Layout for mobile.
 */
export function labelLayoutMobile() {
    const rects = new Map<string | undefined, RectLike>()
    const cb: LabelLayoutOptionCallback = params => {
        if (params.seriesIndex === 1) {
            rects.set(params.text, params.rect)
            return { fontSize: 0 }
        }

        const { x, y, height, width } = params.rect

        let totalWidth = width
        const otherBar = rects.get(params.text)
        const fontSize = Math.min(params.rect.height * 0.5, 14)

        if (otherBar) {
            totalWidth += otherBar.width
        }

        return {
            x: x + totalWidth / 2,
            y: y + height / 2,
            verticalAlign: 'middle',
            align: 'center',
            width: totalWidth,
            fontSize
        }
    }

    return cb
}
